import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Archive,
  ArchiveRestore,
  MoreVertical,
  Pencil,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  CLASSIFICATION_EXPLANATIONS,
  CLASSIFICATION_NAMES,
  RISK_EXPLANATIONS,
  RISK_NAMES,
  type ClassificationVariant,
} from "@/utils/portfolioCardCopy";
import type {
  PortfolioAlert,
  PortfolioCardData,
  PortfolioStep,
  PortfolioStepKey,
  PortfolioStepStatus,
} from "@/utils/portfolioCardData";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useOnboardingModal } from "@/contexts/OnboardingModalContext";
import { getNotificationPdfStatus } from "@/utils/guideProgressCalculator";
import { useCompanyCardStatuses } from "./useCompanyCardStatuses";
import { IstoricEvaluari } from "../IstoricEvaluari";

type CompanyCardProps = {
  card: PortfolioCardData;
  onOpen: () => void;
  onEdit?: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  onDelete: () => void;
  isBusy?: boolean;
};

const STATUS_BADGE: Record<Exclude<PortfolioStepStatus, "loading">, string> = {
  Finalizat: "bg-ds-green-100 text-ds-green-600",
  "În curs": "bg-ds-blue-100 text-ds-blue-700",
  Neînceput: "bg-ds-border-200 text-ds-text-500",
  Blocat: "bg-ds-border-100 text-ds-text-400",
};

const STEP_ROUTES: Record<PortfolioStepKey, (sessionId: string) => string> = {
  notificare: () => "/onboarding/formular-notificare",
  "analiza-risc": (id) => `/analiza-risc/${id}`,
  maturitate: () => "/evaluare-maturitate",
  remediere: () => "/plan-remediere",
};

const STEP_BLOCKER_LABELS: Partial<Record<PortfolioStepKey, string>> = {
  "analiza-risc": "Notificare",
  maturitate: "Analiza Risc",
  remediere: "Maturitate",
};

const CLASSIFICATION_CHIP: Record<ClassificationVariant, string> = {
  ESSENTIAL: "bg-ds-red-50 text-ds-red-700",
  IMPORTANT: "bg-ds-amber-100 text-ds-amber-800",
  OUT_OF_SCOPE: "bg-ds-border-200 text-ds-text-500",
  IMPACT: "bg-ds-amber-100 text-ds-amber-800",
  UNCLASSIFIED: "bg-white text-ds-text-400 border border-ds-border-200",
};

const RISK_CHIP: Record<
  "DE_BAZA" | "IMPORTANT" | "ESENTIAL" | "NEEVALUAT",
  string
> = {
  DE_BAZA: "bg-ds-green-100 text-ds-green-600",
  IMPORTANT: "bg-ds-amber-100 text-ds-amber-800",
  ESENTIAL: "bg-ds-red-50 text-ds-red-700",
  NEEVALUAT: "bg-ds-border-200 text-ds-text-500",
};

function getClassificationVariant(
  classification: PortfolioCardData["classification"]
): ClassificationVariant {
  return classification ?? "UNCLASSIFIED";
}

function formatRevenue(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("ro-RO").format(value);
}

function ProgressStrip({
  steps,
  onStepClick,
}: {
  steps: PortfolioStep[];
  onStepClick: (step: PortfolioStep) => void;
}) {
  return (
    <div
      data-testid="portfolio-card-steps"
      className="mx-3.5 mb-2 overflow-hidden rounded-md border border-ds-border-200"
    >
      <div className="grid grid-cols-4 bg-ds-surface-50 border-b border-ds-border-100">
        {steps.map((step, idx) => (
          <div
            key={`label-${step.key}`}
            className={cn(
              "px-1 py-1 text-center font-mono text-[9px] font-semibold uppercase tracking-wide text-ds-text-500",
              idx < 3 && "border-r border-ds-border-100"
            )}
          >
            {step.label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-4">
        {steps.map((step, idx) => (
          <div
            key={`status-${step.key}`}
            data-testid={`portfolio-step-${step.key}`}
            data-step-status={step.status}
            className={cn(
              "px-1 py-1 text-center",
              idx < 3 && "border-r border-ds-border-100"
            )}
          >
            {step.status === "loading" ? (
              <span
                data-testid="portfolio-step-status-skeleton"
                aria-label="Se încarcă status"
                className="inline-block h-3 w-12 rounded-sm bg-ds-border-200 animate-pulse"
              />
            ) : step.status === "Blocat" ? (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="portfolio-step-status-label"
                    aria-label={`${step.label}: blocat`}
                    className={cn(
                      "inline-block rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-semibold whitespace-nowrap cursor-help focus:outline-none focus:ring-2 focus:ring-ds-blue-500",
                      STATUS_BADGE[step.status]
                    )}
                  >
                    {step.status}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  data-testid={`portfolio-step-locked-popup-${step.key}`}
                  className="w-64"
                >
                  <p className="text-xs text-ds-text-600">
                    Blocat — finalizează{" "}
                    <strong>
                      {STEP_BLOCKER_LABELS[step.key] ?? "etapa anterioară"}
                    </strong>{" "}
                    pentru a debloca {step.label}.
                  </p>
                </PopoverContent>
              </Popover>
            ) : (
              <button
                type="button"
                onClick={() => onStepClick(step)}
                data-testid="portfolio-step-status-label"
                aria-label={`Mergi la ${step.label}`}
                className={cn(
                  "inline-block rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ds-blue-500",
                  STATUS_BADGE[step.status]
                )}
              >
                {step.status}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertsBlock({ alerts }: { alerts: PortfolioAlert[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const urgent = alerts.find((a) => a.days <= 7);
  const summary = urgent
    ? `urgent: ${urgent.days} zile`
    : `${alerts[0]?.days ?? 0} zile`;
  const word = alerts.length === 1 ? "alertă" : "alerte";

  return (
    <div data-testid="portfolio-card-alerts" className="mx-3.5 mb-2">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        data-testid="portfolio-card-alerts-trigger"
        className={cn(
          "flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium",
          urgent
            ? "border-ds-red-500/30 bg-ds-red-50 text-ds-red-700"
            : "border-ds-amber-500/30 bg-ds-amber-100 text-ds-amber-800"
        )}
      >
        <TriangleAlert className="h-3 w-3" aria-hidden="true" />
        <span>
          {alerts.length} {word} — {summary}
        </span>
        <span className="ml-auto text-[10px]">{isOpen ? "▲" : "▼"}</span>
      </button>
      {isOpen && (
        <ul
          data-testid="portfolio-card-alerts-list"
          className="mt-1 overflow-hidden rounded-md border border-ds-border-200"
        >
          {alerts.map((alert, idx) => {
            const isUrgent = alert.days <= 7;
            return (
              <li
                key={`${alert.label}-${idx}`}
                className={cn(
                  "flex justify-between px-2.5 py-1.5 text-[11px]",
                  idx < alerts.length - 1 && "border-b border-ds-border-200",
                  isUrgent
                    ? "bg-ds-red-50 text-ds-red-700"
                    : "bg-ds-amber-100 text-ds-amber-800"
                )}
              >
                <span>{alert.label}</span>
                <span className="font-mono font-semibold">
                  {alert.days} zile — {alert.date}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function CompanyCard({
  card: cardProp,
  onOpen,
  onEdit,
  onArchive,
  onUnarchive,
  onDelete,
  isBusy,
}: CompanyCardProps) {
  const card = useCompanyCardStatuses(cardProp);
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const { switchToSession, currentSessionId } = useSessionSwitcher();
  const isActive = currentSessionId === card.id && !card.isArchived;
  const { showOnboardingModal } = useOnboardingModal();
  const handleStepClick = useCallback(
    async (step: PortfolioStep) => {
      if (
        step.key === "notificare" &&
        getNotificationPdfStatus(card.session.entity_data) !== "completed"
      ) {
        showOnboardingModal({
          onBeforeChoose: () => switchToSession(card.id),
        });
        return;
      }
      const ok = await switchToSession(card.id);
      if (!ok) return;
      navigate(STEP_ROUTES[step.key](card.id));
    },
    [
      card.id,
      card.session.entity_data,
      navigate,
      switchToSession,
      showOnboardingModal,
    ]
  );
  const classificationVariant = getClassificationVariant(card.classification);
  const riskVariant = card.riskLevel ?? "NEEVALUAT";
  const hasUrgentAlert = card.alerts.some((a) => a.days <= 7);
  const sizeDetailLine =
    card.employees !== null || card.revenue !== null
      ? `${card.employees !== null ? `${card.employees} angajați` : "—"}${
          card.revenue !== null
            ? ` · CA: ${formatRevenue(card.revenue)} RON`
            : ""
        }`
      : null;

  return (
    <article
      data-testid={`portfolio-card-${card.id}`}
      data-archived={card.isArchived ? "true" : "false"}
      data-active={isActive ? "true" : "false"}
      role="article"
      aria-current={isActive ? "true" : undefined}
      style={card.isArchived ? { opacity: 0.55 } : undefined}
      className={cn(
        "relative flex flex-col rounded-[10px] border transition-shadow hover:shadow-md",
        isActive ? "bg-ds-blue-50" : "bg-white",
        isActive
          ? "border-ds-blue-300"
          : hasUrgentAlert && !card.isArchived
            ? "border-ds-red-500/30"
            : "border-ds-border-200"
      )}
    >
      {card.isArchived && (
        // Corner ribbon: 64px clip box at the top-left, banner rotated -45°.
        // Banner width 110px > diagonal of 64px box so it spans corner-to-corner.
        <div className="pointer-events-none absolute left-0 top-0 z-10 h-16 w-16 overflow-hidden rounded-tl-[10px]">
          <span
            data-testid="portfolio-card-archived-ribbon"
            className="absolute left-[-30px] top-[14px] w-[110px] rotate-[-45deg] bg-ds-text-500 py-0.5 text-center font-mono text-[9px] uppercase tracking-wide text-white shadow-sm"
          >
            Arhivat
          </span>
        </div>
      )}

      <div className="flex items-start gap-2.5 px-3.5 pb-2 pt-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-navy-900 font-heading text-xs font-bold text-white">
          {card.initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h3
              data-testid="portfolio-card-name"
              className="truncate font-heading text-sm font-bold text-navy-900"
            >
              {card.displayName}
            </h3>
            {card.isNew && !card.isArchived && (
              <span
                data-testid="portfolio-card-new-dot"
                aria-label="Companie nouă"
                title="Companie nouă"
                className="inline-block h-[7px] w-[7px] flex-shrink-0 rounded-full bg-ds-green-500"
              />
            )}
          </div>
          <p className="mt-0.5 font-mono text-[10px] text-ds-text-400">
            {card.cui ? (
              <span data-testid="portfolio-card-cui" className="font-mono">
                {card.cui}
              </span>
            ) : (
              <span className="text-ds-text-400">CUI indisponibil</span>
            )}
            {card.sizeLabel && (
              <>
                {" · "}
                <span>
                  Dimensiune:{" "}
                  <strong className="font-medium text-ds-text-600">
                    {card.sizeLabel}
                  </strong>
                </span>
              </>
            )}
          </p>
          {card.surseLabel && (
            <p
              data-testid="portfolio-card-surse"
              className="mt-0.5 text-[10px] text-ds-text-400"
            >
              {card.surseLabel}
            </p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-1">
          {onEdit && card.actions.canEdit && (
            <button
              type="button"
              onClick={onEdit}
              disabled={isBusy}
              data-testid={`portfolio-card-edit-${card.id}`}
              aria-label={`Editează ${card.displayName}`}
              title="Editează"
              className="flex h-7 w-7 items-center justify-center rounded-md border border-ds-border-200 text-ds-text-400 hover:bg-ds-surface-50 focus:outline-none focus:ring-2 focus:ring-ds-blue-500"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid={`portfolio-card-kebab-${card.id}`}
                aria-label={`Acțiuni pentru ${card.displayName}`}
                disabled={isBusy}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-ds-border-200 text-ds-text-400 hover:bg-ds-surface-50 focus:outline-none focus:ring-2 focus:ring-ds-blue-500"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              data-testid="portfolio-card-kebab-menu"
              className="w-44"
            >
              {card.actions.canOpen && (
                <DropdownMenuItem
                  onClick={onOpen}
                  data-testid="portfolio-kebab-deschide"
                >
                  Deschide
                </DropdownMenuItem>
              )}
              {card.actions.canUnarchive && (
                <DropdownMenuItem
                  onClick={onUnarchive}
                  data-testid="portfolio-kebab-dezarhiveaza"
                  className="gap-2"
                >
                  <ArchiveRestore className="h-4 w-4" />
                  Dezarhivează
                </DropdownMenuItem>
              )}
              {card.actions.canArchive && (
                <DropdownMenuItem
                  onClick={onArchive}
                  data-testid="portfolio-kebab-arhiveaza"
                  className="gap-2"
                >
                  <Archive className="h-4 w-4" />
                  Arhivează
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                data-testid="portfolio-kebab-sterge"
                className="gap-2 text-ds-red-600 focus:text-ds-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Șterge companie
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 px-3.5 pb-2">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="portfolio-card-classification-trigger"
              aria-label={`Încadrare: ${CLASSIFICATION_NAMES[classificationVariant]}`}
              className={cn(
                "rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-semibold focus:outline-none focus:ring-2 focus:ring-ds-blue-500",
                CLASSIFICATION_CHIP[classificationVariant]
              )}
            >
              Încadrare:{" "}
              {CLASSIFICATION_NAMES[classificationVariant].toUpperCase()}
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-testid="portfolio-classification-popup"
            className="w-72"
          >
            <p className="mb-1 font-heading text-sm font-semibold text-navy-900">
              Încadrare și Risc
            </p>
            <p className="mb-2 text-xs text-ds-text-600">
              <strong>{CLASSIFICATION_NAMES[classificationVariant]}:</strong>{" "}
              {CLASSIFICATION_EXPLANATIONS[classificationVariant]}
            </p>
            <p className="text-xs text-ds-text-600">
              <strong>Risc: {RISK_NAMES[riskVariant]}:</strong>{" "}
              {RISK_EXPLANATIONS[riskVariant]}
            </p>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="portfolio-card-risk-trigger"
              aria-label={`Risc: ${RISK_NAMES[riskVariant]}`}
              className={cn(
                "rounded-sm px-1.5 py-0.5 font-mono text-[10px] font-semibold focus:outline-none focus:ring-2 focus:ring-ds-blue-500",
                RISK_CHIP[riskVariant]
              )}
            >
              Risc: {RISK_NAMES[riskVariant]}
            </button>
          </PopoverTrigger>
          <PopoverContent data-testid="portfolio-risk-popup" className="w-72">
            <p className="mb-1 font-heading text-sm font-semibold text-navy-900">
              Încadrare și Risc
            </p>
            <p className="mb-2 text-xs text-ds-text-600">
              <strong>{CLASSIFICATION_NAMES[classificationVariant]}:</strong>{" "}
              {CLASSIFICATION_EXPLANATIONS[classificationVariant]}
            </p>
            <p className="text-xs text-ds-text-600">
              <strong>Risc: {RISK_NAMES[riskVariant]}:</strong>{" "}
              {RISK_EXPLANATIONS[riskVariant]}
            </p>
          </PopoverContent>
        </Popover>

        {card.primarySector && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="portfolio-card-sector-chip"
                aria-label={`Sector principal: ${card.primarySector}${card.secondarySectorCount > 0 ? ` și încă ${card.secondarySectorCount}` : ""}`}
                className="inline-flex items-center gap-1 rounded-sm bg-ds-blue-100 px-1.5 py-0.5 font-mono text-[9px] text-ds-blue-700 focus:outline-none focus:ring-2 focus:ring-ds-blue-500"
              >
                <span>{card.primarySector}</span>
                {card.secondarySectorCount > 0 && (
                  <span data-testid="portfolio-card-sector-extra">
                    +{card.secondarySectorCount}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid="portfolio-sectors-popup"
              className="w-72"
            >
              <p className="mb-2 font-heading text-sm font-semibold text-navy-900">
                Sectoare NIS2
              </p>
              <ul
                data-testid="portfolio-sectors-popup-list"
                className="space-y-1"
              >
                {card.allSectors.map((sector) => (
                  <li
                    key={sector}
                    className="font-mono text-xs text-ds-text-600"
                  >
                    {sector}
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        )}

        <span
          data-testid="portfolio-card-credits"
          className="ml-auto font-mono text-[10px] text-ds-text-400"
        >
          <span className="font-semibold text-ds-text-600">{card.credits}</span>{" "}
          cr
        </span>
      </div>

      <ProgressStrip steps={card.steps} onStepClick={handleStepClick} />

      {card.alerts.length > 0 && <AlertsBlock alerts={card.alerts} />}

      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        data-testid="portfolio-card-expander"
        className="flex w-full items-center justify-center gap-1 border-t border-ds-border-100 bg-ds-surface-50 px-3.5 py-1.5 font-mono text-[10px] text-ds-text-500 hover:bg-ds-surface-100"
      >
        {isExpanded ? "Ascunde detalii ▲" : "Detalii companie ▼"}
      </button>

      {isExpanded && (
        <div
          data-testid="portfolio-card-details"
          className="space-y-2 border-t border-ds-border-100 bg-ds-surface-50 px-3.5 py-2.5 text-xs text-ds-text-600"
        >
          <div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] text-ds-text-400">
                Dimensiune
              </span>
              <span className="font-mono text-[11px] font-semibold text-ds-text-600">
                {card.sizeLabel ?? "—"}
              </span>
            </div>
            {sizeDetailLine && (
              <div className="mt-0.5 font-mono text-[10px] text-ds-text-400">
                <span data-testid="portfolio-detail-employees">
                  {card.employees !== null ? card.employees : "—"}
                </span>
                {" angajați · CA: "}
                <span data-testid="portfolio-detail-revenue">
                  {card.revenue !== null
                    ? `${formatRevenue(card.revenue)} RON`
                    : "—"}
                </span>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] text-ds-text-400">
                Maturitate
              </span>
              <span
                data-testid="portfolio-detail-maturity"
                className={cn(
                  "font-mono text-[11px] font-semibold",
                  card.maturityScore === null
                    ? "text-ds-text-400"
                    : card.maturityTarget !== null &&
                        card.maturityScore >= card.maturityTarget
                      ? "text-ds-green-600"
                      : "text-ds-red-700"
                )}
              >
                {card.maturityScore === null
                  ? "—"
                  : `${card.maturityScore.toFixed(1)}${card.maturityTarget !== null ? ` / ${card.maturityTarget}` : ""}`}
              </span>
            </div>
            {card.maturityDetail && (
              <div className="mt-0.5 font-mono text-[10px] text-ds-text-400">
                {card.maturityDetail}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] text-ds-text-400">
                Nivel Risc
              </span>
              <span
                data-testid="portfolio-detail-risk"
                className="font-mono text-[11px] font-semibold text-ds-text-600"
              >
                {card.riskScore === null && card.riskLevel === null
                  ? "—"
                  : `${RISK_NAMES[riskVariant]}${card.riskScore !== null ? ` (scor ${card.riskScore}/100)` : ""}`}
              </span>
            </div>
            {card.riskDetail && (
              <div className="mt-0.5 font-mono text-[10px] text-ds-text-400">
                {card.riskDetail}
              </div>
            )}
          </div>

          <div>
            <span className="font-mono text-[10px] text-ds-text-400">
              Sectoare NIS2:
            </span>
            {card.allSectors.length > 0 ? (
              <ul
                data-testid="portfolio-detail-sectors"
                className="mt-1 flex flex-wrap gap-1"
              >
                {card.allSectors.map((sector, idx) => (
                  <li
                    key={sector}
                    className={cn(
                      "rounded-sm border px-1.5 py-0.5 font-mono text-[10px]",
                      idx === 0
                        ? "border-ds-blue-500/30 bg-ds-blue-100 text-ds-blue-700"
                        : "border-ds-border-200 bg-white text-ds-text-500"
                    )}
                  >
                    {sector}
                  </li>
                ))}
              </ul>
            ) : (
              <p
                data-testid="portfolio-detail-sectors"
                className="font-medium text-ds-text-700"
              >
                —
              </p>
            )}
          </div>

          <IstoricEvaluari cui={card.cui} />
        </div>
      )}

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-ds-border-100 px-3.5 py-2">
        <span
          data-testid="portfolio-card-last-activity"
          className="font-mono text-[10px] text-ds-text-400"
        >
          {card.lastActivityRelative}
        </span>
        {card.actions.canOpen && (
          <button
            type="button"
            onClick={onOpen}
            disabled={isBusy}
            data-testid="portfolio-card-open"
            className="inline-flex items-center gap-1 rounded-md bg-navy-900 px-3 py-1 font-mono text-[10px] font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
          >
            {isActive ? "Continuă →" : "Deschide →"}
          </button>
        )}
      </div>
    </article>
  );
}
